// Card #3075 - the Confirm Score (#698) decision logic, pulled out of
// ScoringForm.jsx's handleConfirmScore / footer so it can be tested by
// calling it directly instead of mounting the whole form.

/**
 * #169 / #171 / #226 / #2345 - the rows that still need attention before a
 * Confirm Score can go through, in display order (movements, then
 * collectives):
 *   zero    - score === 0 (acknowledgeable)
 *   missing - no score entered (acknowledgeable)
 *   flagged - m.flagged === true (NOT acknowledgeable; must be unflagged)
 * Walks the test's movements so missing rows surface even when the sheet has
 * no entry for them at all.
 */
export function collectConfirmFlags({ sheet, visibleMovements, test, preflightEffectiveTest }) {
  const rowByNum = new Map(
    (sheet?.movementScores ?? []).map((m) => [m.movementNumber, m]),
  );
  const movementFlags = (visibleMovements ?? [])
    .map((mv) => {
      const row = rowByNum.get(mv.movementNumber);
      const base = {
        key: `m${mv.movementNumber}`,
        section: "movement",
        label: `Movement ${mv.movementNumber}`,
        movementNumber: mv.movementNumber,
      };
      if (row?.flagged === true) return { ...base, kind: "flagged" };
      const score = row?.score;
      if (score === 0) return { ...base, kind: "zero" };
      if (typeof score !== "number" || !Number.isFinite(score)) {
        return { ...base, kind: "missing" };
      }
      return null;
    })
    .filter(Boolean);
  // young-horse carve-out mirrors findIncompleteField's: a sheet with NO
  // collectives entered at all is not the sheet that scores them, so its
  // blanks are never flagged as missing here either.
  const collectiveMissingExempt =
    preflightEffectiveTest?.scoringModel === "young-horse-average" &&
    Object.keys(sheet?.collectives ?? {}).length === 0;
  const collectiveFlags = (test?.collectives ?? [])
    .map((c) => {
      const entry = sheet?.collectives?.[c.movementNumber];
      const base = {
        key: `c${c.movementNumber}`,
        section: "collective",
        label: `Collective “${c.name}”`,
        movementNumber: c.movementNumber,
      };
      if (entry?.flagged === true) return { ...base, kind: "flagged" };
      const score = entry?.score;
      if (score === 0) return { ...base, kind: "zero" };
      if (typeof score !== "number" || !Number.isFinite(score)) {
        return collectiveMissingExempt ? null : { ...base, kind: "missing" };
      }
      return null;
    })
    .filter(Boolean);
  return [...movementFlags, ...collectiveFlags];
}

/**
 * What pressing Confirm Score does:
 *   "open-confirm"       - straight to the irreversible Confirm dialog
 *   "blocked-incomplete" - #2810: a movement/collective with no numeric mark
 *                          at all is caught here, before any flag walk or
 *                          network round trip
 *   "resolve"            - the acknowledgeable "Resolve before confirming"
 *                          dialog, with `flags`
 * An eliminated sheet (#208) bypasses the mandatory-field validation
 * entirely.
 */
export function decideConfirmScore({
  eliminated,
  preflightIncompleteField,
  sheet,
  visibleMovements,
  test,
  preflightEffectiveTest,
}) {
  if (eliminated) return { kind: "open-confirm" };
  if (preflightIncompleteField) return { kind: "blocked-incomplete" };
  const flags = collectConfirmFlags({ sheet, visibleMovements, test, preflightEffectiveTest });
  if (flags.length > 0) return { kind: "resolve", flags };
  return { kind: "open-confirm" };
}

/**
 * The two-stage footer's button state (#698, #714, #515): Sign & Complete
 * starts disabled and only unlocks once the marks are confirmed and the
 * confirm write's own local commit has settled; each button shows its
 * spinner only for its own in-flight window.
 */
export function confirmFooterState({
  isCompleted,
  isConfirmed,
  isConfirmingMarks,
  isSigning,
  hasMissingSignature,
}) {
  return {
    confirmLoading: Boolean(isConfirmingMarks),
    confirmDisabled: Boolean(isCompleted || isConfirmed),
    signLoading: Boolean(isSigning),
    signDisabled: Boolean(
      isCompleted || !isConfirmed || isConfirmingMarks || hasMissingSignature,
    ),
  };
}
